import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";

type AuthContextValue = {
    token: string | null | undefined;
    isAuthenticated: boolean | undefined;
    login: (newToken: string) => void;
    logout: () => void;
};

const AuthContext = createContext<AuthContextValue | null>(null);
const AUTH_ERROR_SEARCH_PARAM = "auth_err";
const AUTH_TOKEN_CLEARED_EVENT = "cashbox-auth-token-cleared";

export const AuthProvider = ({ children }: { children?: ReactNode }) => {
    const navigate = useNavigate();
    const [token, setToken] = useState<string | null | undefined>(undefined);
    const [isAuthenticated, setIsAuthenticated] = useState<boolean | undefined>(undefined);

    useEffect(() => {
        const storedToken = localStorage.getItem("auth_token");
        if (storedToken) {
            setToken(storedToken);
            setIsAuthenticated(true);
        }
        else {
            setToken(null);
            setIsAuthenticated(false);
        }
    }, []);

    useEffect(() => {
        const handleTokenCleared = () => {
            setToken(null);
            setIsAuthenticated(false);
        };

        window.addEventListener(AUTH_TOKEN_CLEARED_EVENT, handleTokenCleared);
        return () => {
            window.removeEventListener(AUTH_TOKEN_CLEARED_EVENT, handleTokenCleared);
        };
    }, []);

    const login = (newToken: string) => {
        localStorage.setItem("auth_token", newToken);
        setToken(newToken);
        setIsAuthenticated(true);
    };

    const logout = () => {
        localStorage.removeItem("auth_token");
        setToken(null);
        setIsAuthenticated(false);

        const currSP = new URLSearchParams(window.location.search);
        currSP.delete(AUTH_ERROR_SEARCH_PARAM);
        const currSP_str = currSP.toString();
        navigate(`/login${currSP_str ? "?" + currSP_str : ""}`);
    };

    return (
        <AuthContext.Provider
            value={{
                token,
                isAuthenticated,
                login,
                logout,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = (): AuthContextValue => {
    const ctx = useContext(AuthContext);
    if (!ctx) {
        throw new Error("useAuth must be used inside AuthProvider");
    }
    return ctx;
};
